/**
 * Gamification domain types for the education hub.
 * Defines XP system, achievements, badges, streaks, mastery, and leaderboards.
 */

import type { EducationCategory } from './education';

// =============================================================================
// XP System Types
// =============================================================================

/**
 * XP event types that can trigger XP awards.
 */
export type XPEventType =
  | 'lesson_complete'      // Completed a lesson
  | 'quiz_pass'            // Passed a quiz
  | 'quiz_perfect'         // 100% on a quiz
  | 'quiz_speed_bonus'     // Completed quiz under time limit
  | 'module_complete'      // Completed all lessons in a module
  | 'pathway_complete'     // Completed all modules in a pathway
  | 'review_complete'      // Completed a spaced repetition review
  | 'review_success'       // Successfully recalled in a review (quality >= 3)
  | 'review_perfect'       // Perfect recall in a review (quality = 5)
  | 'streak_milestone'     // Reached a streak milestone (7, 14, 30, etc.)
  | 'daily_challenge'      // Completed daily challenge
  | 'first_lesson'         // First lesson ever completed
  | 'comeback'             // Returned after 7+ days inactive
  | 'achievement_unlock';  // Unlocked an achievement

/**
 * Record of an XP award event.
 */
export interface XPEvent {
  /** Unique event ID */
  id: string;
  /** User ID who earned the XP */
  userId: string;
  /** Type of event that triggered the XP */
  type: XPEventType;
  /** Base XP amount awarded */
  baseXP: number;
  /** Multiplier applied (e.g., streak bonus) */
  multiplier: number;
  /** Final XP awarded (baseXP * multiplier) */
  totalXP: number;
  /** Optional reference to the content (lessonId, quizId, etc.) */
  contentId?: string;
  /** Optional content type */
  contentType?: 'lesson' | 'module' | 'pathway' | 'quiz' | 'review';
  /** Education category for category-based XP tracking */
  category?: EducationCategory;
  /** Timestamp of the event (ISO string) */
  timestamp: string;
  /** Optional metadata (quiz score, time taken, etc.) */
  metadata?: Record<string, unknown>;
}

/**
 * XP configuration for different event types.
 */
export interface XPConfig {
  /** Event type */
  type: XPEventType;
  /** Base XP awarded */
  baseXP: number;
  /** Maximum XP possible with bonuses */
  maxXP?: number;
  /** Whether this event can be earned multiple times per content */
  repeatable: boolean;
  /** Cooldown in hours before same event can be earned again */
  cooldownHours?: number;
}

/**
 * Level definition in the XP progression system.
 */
export interface XPLevel {
  /** Level number (1-based) */
  level: number;
  /** Display name (e.g., "Novice", "Apprentice") */
  name: string;
  /** XP required to reach this level */
  xpRequired: number;
  /** Optional perks unlocked at this level */
  perks?: string[];
}

// =============================================================================
// Streak System Types
// =============================================================================

/**
 * Streak milestone definitions.
 */
export type StreakMilestone = 7 | 14 | 30 | 60 | 100 | 365;

/**
 * Streak data for a user.
 */
export interface StreakData {
  /** Current streak count (days) */
  current: number;
  /** Longest streak ever achieved */
  longest: number;
  /** Date of last activity that counted toward streak (ISO string date only) */
  lastActivityDate: string;
  /** Whether today's activity has been logged */
  todayCompleted: boolean;
  /** Whether the streak is currently frozen (vacation mode) */
  isFrozen: boolean;
  /** Date streak freeze expires (ISO string) */
  freezeExpiresAt?: string;
  /** Number of streak freezes remaining */
  freezesRemaining: number;
  /** Milestones achieved (streak lengths) */
  milestonesAchieved: StreakMilestone[];
}

/**
 * Streak multiplier configuration.
 */
export interface StreakMultiplier {
  /** Minimum streak days for this multiplier */
  minDays: number;
  /** XP multiplier (e.g., 1.5 = 50% bonus) */
  multiplier: number;
}

// =============================================================================
// Achievement System Types
// =============================================================================

/**
 * Achievement category for organization.
 */
export type AchievementCategory =
  | 'learning'    // Lesson/pathway completion achievements
  | 'mastery'     // Skill mastery achievements
  | 'streak'      // Streak-related achievements
  | 'quiz'        // Quiz performance achievements
  | 'social'      // Community/sharing achievements
  | 'special';    // Limited-time or rare achievements

/**
 * Achievement rarity tier.
 */
export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

/**
 * Achievement definition.
 */
export interface Achievement {
  /** Unique achievement ID */
  id: string;
  /** Display name */
  name: string;
  /** Description of how to earn */
  description: string;
  /** Category for filtering */
  category: AchievementCategory;
  /** Rarity tier */
  rarity: AchievementRarity;
  /** Icon name (Lucide icon) */
  icon: string;
  /** XP awarded when unlocked */
  xpReward: number;
  /** Whether this achievement is hidden until unlocked */
  isSecret: boolean;
  /** Progress requirement (e.g., 10 for "Complete 10 lessons") */
  progressRequired: number;
  /** Progress type for tracking */
  progressType: AchievementProgressType;
  /** Optional: specific content required (e.g., pathway ID) */
  requiredContentId?: string;
  /** Sort order within category */
  order: number;
}

/**
 * Progress type for achievement tracking.
 */
export type AchievementProgressType =
  | 'lessons_completed'
  | 'modules_completed'
  | 'pathways_completed'
  | 'quizzes_passed'
  | 'perfect_quizzes'
  | 'streak_days'
  | 'total_xp'
  | 'reviews_completed'
  | 'daily_challenges'
  | 'category_lessons'    // Lessons in specific category
  | 'category_mastery'    // Mastery in specific category
  | 'login_days'
  | 'custom';             // Custom logic required

/**
 * User's progress on an achievement.
 */
export interface UserAchievement {
  /** Achievement ID */
  achievementId: string;
  /** User ID */
  userId: string;
  /** Current progress toward the achievement */
  progress: number;
  /** Whether the achievement is unlocked */
  isUnlocked: boolean;
  /** When the achievement was unlocked (ISO string) */
  unlockedAt?: string;
  /** Whether the user has seen the unlock notification */
  hasSeenNotification: boolean;
}

// =============================================================================
// Badge System Types
// =============================================================================

/**
 * Badge definition (visual representation of achievements).
 */
export interface Badge {
  /** Unique badge ID (usually matches achievement ID) */
  id: string;
  /** Display name */
  name: string;
  /** Category for display grouping */
  category: AchievementCategory;
  /** Rarity determines visual treatment */
  rarity: AchievementRarity;
  /** Icon name (Lucide icon) */
  icon: string;
  /** Gradient colors for badge background (Tailwind classes) */
  gradientColors: string;
  /** Associated achievement ID (if any) */
  achievementId?: string;
  /** Description shown on badge detail */
  description: string;
}

// =============================================================================
// Mastery System Types
// =============================================================================

/**
 * Mastery level thresholds.
 */
export type MasteryLevel = 'novice' | 'beginner' | 'intermediate' | 'proficient' | 'expert' | 'master';

/**
 * Concept mastery tracking.
 */
export interface ConceptMastery {
  /** Concept ID (can be category, topic, or lesson-specific) */
  conceptId: string;
  /** User ID */
  userId: string;
  /** Current mastery score (0-100) */
  score: number;
  /** Mastery level based on score */
  level: MasteryLevel;
  /** Number of times this concept has been practiced */
  practiceCount: number;
  /** Number of correct responses */
  correctCount: number;
  /** Last practice date (ISO string) */
  lastPracticedAt: string;
  /** Next review date based on spaced repetition (ISO string) */
  nextReviewAt: string;
  /** Ease factor for spaced repetition (SM-2 algorithm) */
  easeFactor: number;
  /** Current interval in days */
  intervalDays: number;
}

/**
 * Mastery score thresholds.
 */
export const MASTERY_THRESHOLDS: Record<MasteryLevel, number> = {
  novice: 0,
  beginner: 20,
  intermediate: 40,
  proficient: 60,
  expert: 80,
  master: 95,
};

// =============================================================================
// Leaderboard Types
// =============================================================================

/**
 * Leaderboard time period.
 */
export type LeaderboardPeriod = 'daily' | 'weekly' | 'monthly' | 'allTime';

/**
 * Leaderboard type/category.
 */
export type LeaderboardType = 'xp' | 'streak' | 'quizzes' | 'lessons';

/**
 * Leaderboard entry for a user.
 */
export interface LeaderboardEntry {
  /** User ID */
  userId: string;
  /** Display name */
  displayName: string;
  /** Optional avatar URL */
  avatarUrl?: string;
  /** Rank position (1-based) */
  rank: number;
  /** Previous rank (for showing change) */
  previousRank?: number;
  /** Score/value for this leaderboard */
  score: number;
  /** User's current level */
  level: number;
  /** User's current streak (for context) */
  streak?: number;
}

/**
 * Full leaderboard data.
 */
export interface Leaderboard {
  /** Leaderboard type */
  type: LeaderboardType;
  /** Time period */
  period: LeaderboardPeriod;
  /** Top entries (usually top 10-50) */
  entries: LeaderboardEntry[];
  /** Current user's entry (may not be in top entries) */
  currentUserEntry?: LeaderboardEntry;
  /** Total participants */
  totalParticipants: number;
  /** When the leaderboard was last updated (ISO string) */
  lastUpdatedAt: string;
  /** When the period started (ISO string) */
  periodStartAt: string;
  /** When the period ends (ISO string) */
  periodEndAt: string;
}

// =============================================================================
// User Gamification Profile
// =============================================================================

/**
 * Complete gamification profile for a user.
 * Stored in Supabase
 */
export interface UserGamificationProfile {
  /** User ID */
  userId: string;

  // XP & Level
  /** Total XP earned all time */
  totalXP: number;
  /** Current level */
  level: number;
  /** XP earned in current level (progress to next) */
  xpInCurrentLevel: number;
  /** XP required to reach next level */
  xpToNextLevel: number;
  /** XP broken down by category */
  xpByCategory: Record<EducationCategory, number>;

  // Streaks
  /** Streak data */
  streak: StreakData;

  // Stats
  /** Total lessons completed */
  lessonsCompleted: number;
  /** Total quizzes passed */
  quizzesPassed: number;
  /** Total perfect quizzes (100%) */
  perfectQuizzes: number;
  /** Total daily challenges completed */
  dailyChallengesCompleted: number;
  /** Total reviews completed */
  reviewsCompleted: number;
  /** Total time spent learning (minutes) */
  totalTimeSpent: number;

  // Timestamps
  /** Profile creation date (ISO string) */
  createdAt: string;
  /** Last activity date (ISO string) */
  lastActivityAt: string;
  /** Last XP earned date (ISO string) */
  lastXPEarnedAt?: string;
}

/**
 * Partial update for gamification profile.
 */
export type UserGamificationProfileUpdate = Partial<Omit<UserGamificationProfile, 'userId' | 'createdAt'>>;

// =============================================================================
// Daily Challenge Types
// =============================================================================

/**
 * Daily challenge definition.
 */
export interface DailyChallenge {
  /** Challenge ID (usually date-based: "2024-01-15") */
  id: string;
  /** Challenge date (ISO date string, no time) */
  date: string;
  /** Challenge title */
  title: string;
  /** Challenge description */
  description: string;
  /** Category/topic focus */
  category: EducationCategory;
  /** Difficulty level */
  difficulty: 'easy' | 'medium' | 'hard';
  /** Time limit in seconds */
  timeLimitSeconds: number;
  /** Number of questions */
  questionCount: number;
  /** XP reward for completion */
  xpReward: number;
  /** Bonus XP for perfect score */
  perfectBonus: number;
  /** Quiz ID to use */
  quizId: string;
}

/**
 * User's daily challenge attempt.
 */
export interface DailyChallengeAttempt {
  /** Attempt ID */
  id: string;
  /** User ID */
  userId: string;
  /** Challenge ID */
  challengeId: string;
  /** Challenge date */
  date: string;
  /** Score achieved (0-100) */
  score: number;
  /** Time taken in seconds */
  timeTaken: number;
  /** Whether the challenge was completed */
  completed: boolean;
  /** XP earned from this attempt */
  xpEarned: number;
  /** Timestamp of attempt (ISO string) */
  attemptedAt: string;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * XP awards for different events.
 */
export const XP_AWARDS: Record<XPEventType, XPConfig> = {
  lesson_complete: { type: 'lesson_complete', baseXP: 50, repeatable: false },
  quiz_pass: { type: 'quiz_pass', baseXP: 75, maxXP: 150, repeatable: true, cooldownHours: 24 },
  quiz_perfect: { type: 'quiz_perfect', baseXP: 50, repeatable: true, cooldownHours: 24 },
  quiz_speed_bonus: { type: 'quiz_speed_bonus', baseXP: 25, repeatable: true, cooldownHours: 24 },
  module_complete: { type: 'module_complete', baseXP: 150, repeatable: false },
  pathway_complete: { type: 'pathway_complete', baseXP: 500, repeatable: false },
  review_complete: { type: 'review_complete', baseXP: 30, repeatable: true },
  review_success: { type: 'review_success', baseXP: 15, repeatable: true },
  review_perfect: { type: 'review_perfect', baseXP: 25, repeatable: true },
  streak_milestone: { type: 'streak_milestone', baseXP: 100, maxXP: 500, repeatable: false },
  daily_challenge: { type: 'daily_challenge', baseXP: 100, maxXP: 200, repeatable: false },
  first_lesson: { type: 'first_lesson', baseXP: 100, repeatable: false },
  comeback: { type: 'comeback', baseXP: 50, repeatable: true, cooldownHours: 168 }, // 7 days
  achievement_unlock: { type: 'achievement_unlock', baseXP: 0, repeatable: false }, // XP from achievement itself
};

/**
 * Streak XP multipliers.
 */
export const STREAK_MULTIPLIERS: StreakMultiplier[] = [
  { minDays: 0, multiplier: 1.0 },
  { minDays: 3, multiplier: 1.1 },
  { minDays: 7, multiplier: 1.25 },
  { minDays: 14, multiplier: 1.5 },
  { minDays: 30, multiplier: 1.75 },
  { minDays: 60, multiplier: 2.0 },
];

/**
 * Level progression (cumulative XP required).
 */
export const XP_LEVELS: XPLevel[] = [
  { level: 1, name: 'Novice', xpRequired: 0 },
  { level: 2, name: 'Apprentice', xpRequired: 100 },
  { level: 3, name: 'Student', xpRequired: 250 },
  { level: 4, name: 'Learner', xpRequired: 500 },
  { level: 5, name: 'Scholar', xpRequired: 1000 },
  { level: 6, name: 'Practitioner', xpRequired: 1750 },
  { level: 7, name: 'Adept', xpRequired: 2750 },
  { level: 8, name: 'Expert', xpRequired: 4000 },
  { level: 9, name: 'Master', xpRequired: 5500 },
  { level: 10, name: 'Grandmaster', xpRequired: 7500 },
  { level: 11, name: 'Sage', xpRequired: 10000 },
  { level: 12, name: 'Virtuoso', xpRequired: 13000 },
  { level: 13, name: 'Legend', xpRequired: 17000 },
  { level: 14, name: 'Mythic', xpRequired: 22000 },
  { level: 15, name: 'Transcendent', xpRequired: 30000 },
];

/**
 * Calculate level from total XP.
 */
export function calculateLevel(totalXP: number): { level: number; name: string; xpInLevel: number; xpToNext: number } {
  let currentLevel = XP_LEVELS[0];
  let nextLevel = XP_LEVELS[1];

  for (let i = 0; i < XP_LEVELS.length - 1; i++) {
    if (totalXP >= XP_LEVELS[i].xpRequired && totalXP < XP_LEVELS[i + 1].xpRequired) {
      currentLevel = XP_LEVELS[i];
      nextLevel = XP_LEVELS[i + 1];
      break;
    }
    if (i === XP_LEVELS.length - 2 && totalXP >= XP_LEVELS[i + 1].xpRequired) {
      currentLevel = XP_LEVELS[i + 1];
      nextLevel = { level: currentLevel.level + 1, name: 'Beyond', xpRequired: currentLevel.xpRequired + 10000 };
    }
  }

  const xpInLevel = totalXP - currentLevel.xpRequired;
  const xpToNext = nextLevel.xpRequired - totalXP;

  return {
    level: currentLevel.level,
    name: currentLevel.name,
    xpInLevel,
    xpToNext,
  };
}

/**
 * Calculate streak multiplier from current streak.
 */
export function calculateStreakMultiplier(streakDays: number): number {
  let multiplier = 1.0;
  for (const config of STREAK_MULTIPLIERS) {
    if (streakDays >= config.minDays) {
      multiplier = config.multiplier;
    }
  }
  return multiplier;
}

/**
 * Calculate mastery level from score.
 */
export function calculateMasteryLevel(score: number): MasteryLevel {
  if (score >= MASTERY_THRESHOLDS.master) return 'master';
  if (score >= MASTERY_THRESHOLDS.expert) return 'expert';
  if (score >= MASTERY_THRESHOLDS.proficient) return 'proficient';
  if (score >= MASTERY_THRESHOLDS.intermediate) return 'intermediate';
  if (score >= MASTERY_THRESHOLDS.beginner) return 'beginner';
  return 'novice';
}
